import Modal from "@/UI/Modal";
import ViewBtn from "@/components/Buttons/ViewBtn";
import CommonDelete from "@/components/CommonUi/CommonDelete";
import CreateDrawer from "@/components/CommonUi/CreateDrawer";
import BulkDelete from "@/components/CommonUi/BulkDelete";
import TableComponent from "@/components/CommonUi/TableComponent";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import { AdvancedFilter } from "@/components/CommonUi/AdvancedFilter";
import { apiSlice } from "@/redux/rtk/features/apiSlice/apiSlice";
import { useGetLeadsPaginatedQuery } from "@/redux/rtk/features/leads/leadsApi";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineDelete } from "react-icons/ai";
import { CiEdit } from "react-icons/ci";

import { FaWhatsapp } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import AddLeads from "./AddLeads";
import UpdateLead from "./UpdateLead";
import useLeadFilter from "./useLeadFilter";
import { Button } from "antd";
import BulkUpdateLeadSource from "./BulkUpdateLeadSource";
import BulkUpdateLeadStatus from "./BulkUpdateLeadStatus";
import BulkUpdateLeadOwner from "./BulkUpdateLeadOwner";
import BulkUpdateLeadDetails from "./BulkUpdateLeadDetails";
import SyncGoogleSheetModal from "./SyncGoogleSheetModal";
import { SiGooglesheets } from "react-icons/si";

export default function GetAllLeads() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pageConfig, setPageConfig] = useState({
    page: 1,
    count: 10,
  });
  const { data, isLoading } = useGetLeadsPaginatedQuery(pageConfig);
  const [edit, setEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkUpdateSourceOpen, setBulkUpdateSourceOpen] = useState(false);
  const [bulkUpdateStatusOpen, setBulkUpdateStatusOpen] = useState(false);
  const [bulkUpdateOwnerOpen, setBulkUpdateOwnerOpen] = useState(false);
  const [bulkUpdateDetailsOpen, setBulkUpdateDetailsOpen] = useState(false);
  const [syncGoogleSheetOpen, setSyncGoogleSheetOpen] = useState(false);

  /* eslint-disable-next-line */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const advancedFilters = useLeadFilter(filtersOpen);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const hasSelected = selectedRowKeys.length > 0;

  const columns = [
    {
      title: t("common.id"),
      key: "id",
      dataIndex: "id",
    },
    {
      title: t("lead.firstName"),
      key: "firstName",
      dataIndex: "firstName",
      render: (firstName, item) => (
        <Link to={`/admin/lead/${item?.id}`}>{firstName}</Link>
      ),
      renderCsv: (firstName) => firstName,
    },
    {
      title: t("lead.lastName"),
      key: "lastName",
      dataIndex: "lastName",
    },
    {
      title: t("common.email"),
      key: "email",
      dataIndex: "email",
    },
    {
      title: t("lead.phoneNumber"),
      key: "phone",
      dataIndex: "phone",
      render: (phone) => (
        <div className="flex items-center gap-2">
          <span>{phone || "-"}</span>
          {phone && (
            <a
              href={`https://wa.me/${phone.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="text-green-500 hover:text-green-600 text-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <FaWhatsapp />
            </a>
          )}
        </div>
      ),
      renderCsv: (phone) => phone,
    },
    {
      title: t("lead.mobile"),
      key: "mobile",
      dataIndex: "mobile",
      render: (mobile) => (
        <div className="flex items-center gap-2">
          <span>{mobile || "-"}</span>
          {mobile && (
            <a
              href={`https://wa.me/${mobile.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="text-green-500 hover:text-green-600 text-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <FaWhatsapp />
            </a>
          )}
        </div>
      ),
      renderCsv: (mobile) => mobile,
    },
    {
      title: t("lead.country"),
      key: "country",
      dataIndex: "country",
    },
    {
      title: t("lead.country_of_study"),
      key: "country_of_study",
      dataIndex: "countryOfStudy",
      render: (val) => val || "-",
      renderCsv: (val) => val || "-",
    },
    {
      title: t("lead.nationality"),
      key: "nationality",
      dataIndex: "nationality",
    },
    {
      title: t("lead.leadOwner"),
      dataIndex: "leadOwner",
      key: "leadOwner",
      render: (owner) => (typeof owner === "object" ? owner?.username : owner) || "-",
      renderCsv: (owner) => (typeof owner === "object" ? owner?.username : owner) || "-",
    },
    {
      title: t("lead.leadSource"),
      key: "leadSourceName",
      dataIndex: "leadSourceName",
      render: (val) => val || "-",
      renderCsv: (val) => val || "-",
    },
    {
      title: t("lead.leadStatus"),
      key: "leadStatus",
      dataIndex: "leadStatus",
      render: (status, item) => item?.leadStatusName || status || "-",
      renderCsv: (status, item) => item?.leadStatusName || status || "-",
    },
    {
      title: t("lead.interest"),
      key: "interestName",
      dataIndex: "interestName",
      render: (val) => val || "-",
      renderCsv: (val) => val || "-",
    },
    {
      title: t("lead.major"),
      key: "majorName",
      dataIndex: "majorName",
      render: (val) => val || "-",
      renderCsv: (val) => val || "-",
    },
    {
      title: t("lead.adId"),
      key: "adId",
      dataIndex: "adId",
      renderCsv: (val) => val,
    },
    {
      title: t("lead.campaignCode"),
      key: "campaignCode",
      dataIndex: "campaignCode",
      renderCsv: (val) => val,
    },
    {
      title: t("lead.requiredLanguage"),
      key: "language_required",
      dataIndex: "languageRequired",
      render: (lang) => lang?.toUpperCase() || "-",
      renderCsv: (lang) => lang?.toUpperCase() || "-",
    },
    {
      title: t("lead.degree"),
      key: "degree",
      dataIndex: "degree",
      renderCsv: (val) => val,
    },
    {
      title: t("lead.description"),
      key: "description",
      dataIndex: "description",
      ellipsis: true,
      renderCsv: (val) => val,
    },
    {
      title: t("lead.convertStatus"),
      key: "isConverted",
      dataIndex: "isConverted",
      render: (isConverted) =>
        isConverted === "true" ? t("lead.converted") : t("lead.notConverted"),
      renderCsv: (isConverted) =>
        isConverted === "true" ? t("lead.converted") : t("lead.notConverted"),
    },
    {
      title: t("common.createdAt"),
      key: "createdAt",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
      renderCsv: (date) => new Date(date).toLocaleString(),
    },
    {
      title: t("common.updatedAt"),
      key: "updatedAt",
      dataIndex: "updatedAt",
      render: (date) => new Date(date).toLocaleString(),
      renderCsv: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "",
      key: "action",
      render: (item) => [
        {
          label: <ViewBtn title={t("common.view")} path={`/admin/lead/${item?.id}`} />,
          key: "view",
        },
        {
          label: (
            <CommonDelete
              id={item?.id}
              title={t("common.delete")}
              permission={"delete-lead"}
              deleteThunk={apiSlice.endpoints.deleteLead.initiate}
              query={pageConfig}
              className="bg-white text-black"
              icon={<AiOutlineDelete />}
            />
          ),
          key: "delete",
        },
      ],
      csvOff: true,
    },
  ];

  return (
    <UserPrivateComponent permission={"readAll-lead"} altPermission={"readSingle-lead"}>
      <TableComponent
        list={data?.getAllLead}
        columns={columns}
        loading={isLoading}
        total={data?.totalLead}
        setPageConfig={setPageConfig}
        pageConfig={pageConfig}
        title={t("lead.leadList") + " " + t("common.list")}
        isSearch
        filters={advancedFilters}
        exportPermission="export-lead"
        printPermission="print-lead"
        columnKey="leads_table"
        componentTitle={t("lead.title")}
        extraFilter={
          <AdvancedFilter
            page="lead"
            filters={advancedFilters}
            pageConfig={pageConfig}
            setPageConfig={setPageConfig}
            onOpen={() => setFiltersOpen(true)}
          />
        }
        extra={
          <div className="flex gap-2 items-center">
            {hasSelected && (
              <>
                <Button onClick={() => setBulkUpdateSourceOpen(true)}>
                  {t("lead.bulkUpdateSource")}
                </Button>
                <Button onClick={() => setBulkUpdateStatusOpen(true)}>
                  {t("lead.bulkUpdateStatus")}
                </Button>
                <Button onClick={() => setBulkUpdateOwnerOpen(true)}>
                  {t("lead.bulkUpdateOwner")}
                </Button>
                <Button onClick={() => setBulkUpdateDetailsOpen(true)}>
                  {t("lead.bulkUpdateDetails")}
                </Button>
                <BulkDelete
                  selectedRowKeys={selectedRowKeys}
                  permission={"delete-lead"}
                  deleteThunk={apiSlice.endpoints.deleteLead.initiate}
                  query={pageConfig}
                  clearSelection={() => setSelectedRowKeys([])}
                />
                <span className="ml-2">
                  {hasSelected ? `${selectedRowKeys.length} selected` : ""}
                </span>
              </>
            )}
            <UserPrivateComponent permission={"create-lead"}>
              <Button
                onClick={() => setSyncGoogleSheetOpen(true)}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-3 py-1.5 rounded text-sm shadow-sm"
              >
                <SiGooglesheets className="text-base" />
                <span>{t("lead.syncGoogleSheet")}</span>
              </Button>
            </UserPrivateComponent>
            <CreateDrawer permission={"create-lead"} title={t("lead.createLead")}>
              <AddLeads pageConfig={pageConfig} />
            </CreateDrawer>
          </div>
        }
        rowSelection={rowSelection}
        onRow={(item) => ({
  onClick: (event) => {
    // Prevent navigation when user is selecting text (e.g., long press and drag to copy)
    const selection = window.getSelection?.().toString();
    if (selection && selection.length > 0) {
      return; // Do not navigate, allow copy
    }
    navigate(`/admin/lead/${item?.id}`);
  },
  className: "cursor-pointer",
})}
      />
      <Modal
        open={syncGoogleSheetOpen}
        onClose={() => setSyncGoogleSheetOpen(false)}
        title={t("lead.syncGoogleSheet")}
        className="md:w-[48%] px-5"
      >
        <SyncGoogleSheetModal onClose={() => setSyncGoogleSheetOpen(false)} />
      </Modal>
      <Modal
        open={bulkUpdateSourceOpen}
        onClose={() => setBulkUpdateSourceOpen(false)}
        title="Bulk Update Lead Source"
        className="md:w-[40%] px-5">
        <BulkUpdateLeadSource
          selectedRowKeys={selectedRowKeys}
          onClose={() => setBulkUpdateSourceOpen(false)}
          clearSelection={() => setSelectedRowKeys([])}
        />
      </Modal>
      <Modal
        open={bulkUpdateStatusOpen}
        onClose={() => setBulkUpdateStatusOpen(false)}
        title="Bulk Update Lead Status"
        className="md:w-[40%] px-5">
        <BulkUpdateLeadStatus
          selectedRowKeys={selectedRowKeys}
          onClose={() => setBulkUpdateStatusOpen(false)}
          clearSelection={() => setSelectedRowKeys([])}
        />
      </Modal>
      <Modal
        open={bulkUpdateOwnerOpen}
        onClose={() => setBulkUpdateOwnerOpen(false)}
        title={t("lead.bulkUpdateOwner")}
        className="md:w-[40%] px-5">
        <BulkUpdateLeadOwner
          selectedRowKeys={selectedRowKeys}
          onClose={() => setBulkUpdateOwnerOpen(false)}
          clearSelection={() => setSelectedRowKeys([])}
        />
      </Modal>
      <Modal
        open={bulkUpdateDetailsOpen}
        onClose={() => setBulkUpdateDetailsOpen(false)}
        title={t("lead.bulkUpdateDetails")}
        className="md:w-[40%] px-5">
        <BulkUpdateLeadDetails
          selectedRowKeys={selectedRowKeys}
          onClose={() => setBulkUpdateDetailsOpen(false)}
          clearSelection={() => setSelectedRowKeys([])}
        />
      </Modal>
      <Modal
        open={edit}
        onClose={() => setEdit(false)}
        className="md:w-[40%] px-5 "
        title={t("lead.editLead")}>
        <UpdateLead data={edit} onClose={() => setEdit(false)} />
      </Modal>
    </UserPrivateComponent>
  );
}
